// src/js/utils/instants.ts
// Phase 240 (240-01, D-01/D-03/D-05) — Instant formatting, single source of truth.
//
// Extracted from src/js/shared.ts (Phase 180/181-era `localizeInstants()`), which owned this
// logic as module-private helpers. Every relative/absolute instant format in the app now lives
// here, and here only — no second copy is added anywhere (D-02/D-03/D-05).
//
// ZERO imports, deliberately (240-01 deviation notice): shared.ts imports bootstrap /
// datatables.net-bs5 / @melloware/coloris / picker (flatpickr) / utils/chartTheme (Highcharts) at
// module top, every one of which touches document/window on import — so a dependency-free module
// is the only way this file can be exercised by `node tests/mc/*.test.ts` with no jsdom and no
// bundler (D-08 proof 1). shared.ts imports from this module and keeps owning the 60s ticker and
// the DataTable.defaults.drawCallback re-sweep hook.
//
// XSS mitigation T-180-01/T-240-01: localizeInstant() writes only textContent/title/dataset — the
// raw-HTML DOM sink is never touched.

// ── RTF singleton (D-03, native Intl, zero bundle cost) ───────────────────────
const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto', style: 'long' });

/**
 * Relative age phrase for an elapsed-milliseconds gap.
 *
 * Phase 240 code review CR-02 — the ladder runs all the way to `year`, and it MUST. Before this
 * helper existed the same job was done by `mc.ts`'s and `feed.ts`'s own `relativeTime()` helpers,
 * BOTH of which carried day and month tiers; 240-01 deleted them and 240-04's new
 * `data-relative="always"` opt-out (D-01) then routed UNBOUNDED ages through what was an
 * hour-capped ladder. The archived-servers accordion (`default.latte:267`) lists servers archived
 * arbitrarily long ago and was reading "8,760 hours ago" for a year, live on prod. Capping this at
 * `hour` again is a regression against shipped behaviour, not a simplification.
 *
 * Additive by construction: a non-`always` node cuts over to `formatAbsolute()` at 24h inside
 * `localizeInstant()` and therefore never reaches the day/month/year branches — so extending the
 * ladder changes nothing for the app-wide default and everything for the D-01 opt-out surfaces.
 *
 * Unit boundaries deliberately use the AVERAGE Gregorian month (2 629 800 000 ms) and year
 * (31 557 600 000 ms) rather than 30/365-day approximations, so the month->year handoff at
 * `diffMonth === 12` lands exactly on 365.25 days with no gap.
 */
export function formatRelative(diffMs: number): string {
    const diffSec   = Math.round(diffMs / 1000);
    const diffMin   = Math.round(diffMs / 60_000);
    const diffH     = Math.round(diffMs / 3_600_000);
    const diffDay   = Math.round(diffMs / 86_400_000);
    const diffMonth = Math.round(diffMs / 2_629_800_000);   // avg Gregorian month
    if (diffSec < 60)   return RTF.format(-diffSec, 'second');
    if (diffMin < 60)   return RTF.format(-diffMin, 'minute');
    if (diffH < 24)     return RTF.format(-diffH, 'hour');
    if (diffDay < 30)   return RTF.format(-diffDay, 'day');
    if (diffMonth < 12) return RTF.format(-diffMonth, 'month');
    return RTF.format(-Math.round(diffMs / 31_557_600_000), 'year'); // avg Gregorian year
}

export function formatAbsolute(date: Date): string {
    return new Intl.DateTimeFormat('en', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(date);
}

export function formatTooltip(date: Date): string {
    return new Intl.DateTimeFormat('en', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short',
    }).format(date);
}

/**
 * Phase 240 code review CR-01 — reinterpret a WIRE instant (a poll-JSON timestamp) as UTC.
 *
 * `App\Mc\ServerStatus::$lastSeenAt` / `$cachedAt` are built as `$row->last_seen_at->format('c')`
 * on a **Nette-hydrated** DATETIME (`McModel.php:270`, `cron/mc/snapshot.php:321`), with a
 * `(string)` cast fallback for the non-hydrated case. Per this repo's recorded scar
 * (`feedback_nette_hydrated_datetime_gettimestamp_shifts_tz.md`, and Tz's own class docblock) that
 * object carries the *naive-UTC DB digits* under the *runtime Toronto* timezone — so the `-04:00`
 * in `2026-07-30T08:00:00-04:00` is a LIE and `new Date(wire)` lands 4 hours in the future.
 *
 * The server-rendered mirror of the very same field does NOT have this problem: `default.latte` /
 * `detail.latte` run the value through `Tz::isoToDate()` + `Tz::toUtcIso()`, and `toUtcIso()`
 * re-formats in the object's OWN timezone before re-stamping as `Z` — which hands back the naive
 * digits. This helper performs that exact same reinterpretation client-side (strip whatever
 * designator is present, re-stamp as Z) so the poll-rendered `<time data-utc>` and the
 * server-rendered one name the SAME instant instead of drifting by the runtime offset.
 *
 * Handles both shapes McModel can emit: the `->format('c')` shape (`T` separator + offset) and the
 * `(string)` cast fallback (`Y-m-d H:i:s`, space separator, no designator) — matching
 * `Tz::isoToDate()`'s own two explicit branches.
 *
 * Degrades to `null` on null/empty/malformed input rather than throwing or guessing: a bad stored
 * timestamp must never break a fleet card, and it must never be silently rendered as a plausible
 * wrong time.
 */
export function wireInstantToUtcIso(wire: string | null | undefined): string | null {
    if (wire === null || wire === undefined) return null;
    const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/.exec(wire);
    return m === null ? null : `${m[1]}T${m[2]}Z`;
}

/**
 * Phase 257 (257-03, D-12) — the ONLY place localizeInstant() ever assigns tooltip text.
 *
 * Bootstrap's Tooltip constructor calls `_fixTitle()` exactly ONCE at construction, moving `title`
 * into `data-bs-original-title` and removing `title`; `_getTitle()` then reads
 * `data-bs-original-title` fresh on every show. If an opted-in element kept having its `title`
 * attribute rewritten on every re-age (the 60s ticker, a 30s poll tick, a DataTables draw
 * callback), the native browser tooltip would resurrect alongside the styled one — two tooltips at
 * once — while `data-bs-original-title` stayed frozen at whatever it was at construction time.
 *
 * Gated on `data-bs-toggle="tooltip"` so this is deliberately inert for every element that has not
 * opted in — today that is every `time[data-utc]` surface in the app except one:
 * `app/Presenters/templates/Mc/default.latte`'s "Last seen" `<time>` element (the fleet-list
 * offline card), the first and currently only opt-in site.
 */
function setInstantTooltip(el: HTMLElement, text: string): void {
    if (el.getAttribute('data-bs-toggle') === 'tooltip') {
        el.setAttribute('data-bs-original-title', text);
        el.removeAttribute('title');
        return;
    }
    el.title = text;
}

/**
 * Rewrite a single time[data-utc] element to the viewer's local timezone (D-03 — split out of
 * localizeInstants() so a caller can localize one freshly-created <time> node without re-walking
 * the whole page).
 * Idempotent: absolute nodes (>=24h) are flagged data-localized="1" after first run (D-09).
 * Relative nodes (<24h, or opted out via data-relative="always" — D-01) are re-aged on every call
 * (ticker + draw re-sweep).
 * XSS mitigation T-180-01/T-240-01: only textContent/title/dataset are written — the raw-HTML DOM
 * sink is never touched.
 */
export function localizeInstant(el: HTMLTimeElement): void {
    const now = Date.now();
    const TWENTY_FOUR_H = 86_400_000; // 24h in ms (D-01)
    if (el.dataset.localized === '1') return; // idempotency guard — absolute nodes (D-09)
    const iso = el.getAttribute('data-utc');
    if (!iso) return;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return; // invalid ISO — leave server fallback (D-07/T-180-02)
    const diffMs = now - date.getTime();
    if (diffMs <= 60_000) {
        // future timestamps or <60s ago → "just now" (D-04: clock-skew + <60s clamp) — universal,
        // NOT gated by the D-01 opt-out.
        el.textContent = 'just now';
        setInstantTooltip(el, formatTooltip(date));
        // relative node — intentionally NOT marked data-localized (ticker re-ages)
    } else if (diffMs < TWENTY_FOUR_H || el.dataset.relative === 'always') {
        // 1 min to <24h → relative format (D-03), OR the surface opted out of the 24h cutover via
        // data-relative="always" (D-01 — e.g. "Last seen", which is about the elapsed gap, not
        // the date). The app-wide default for any other time[data-utc] is unchanged.
        el.textContent = formatRelative(diffMs);
        setInstantTooltip(el, formatTooltip(date));
        // relative node — intentionally NOT marked data-localized (ticker re-ages)
    } else {
        // >=24h, no opt-out → absolute format (D-01/D-02)
        el.textContent = formatAbsolute(date);
        setInstantTooltip(el, formatTooltip(date));
        el.dataset.localized = '1'; // freeze — ticker + draw skip this node (D-09)
    }
}

/**
 * Rewrite every time[data-utc] element on the page to the viewer's local timezone. Thin loop over
 * localizeInstant() (D-03).
 */
export function localizeInstants(): void {
    document.querySelectorAll<HTMLTimeElement>('time[data-utc]').forEach(localizeInstant);
}

/**
 * Composed "absolute instant — relative age" header string for a chart tooltip (D-05), e.g.
 * "Jul 29, 2026, 1:24 PM EDT — 4 hours ago". One named export so every in-scope chart
 * (resourceHistory.ts / analytics.ts / profile.ts) shares the exact same hover-timestamp format
 * app-wide, chart or not — CONTEXT's "one named export is preferred" discretion call.
 */
export function chartInstantHeader(epochMs: number): string {
    const date = new Date(epochMs);
    return `${formatTooltip(date)} — ${formatRelative(Date.now() - epochMs)}`;
}
